import { faCartPlus, faInfo } from "@fortawesome/pro-solid-svg-icons";
import {
	Anchor,
	Button,
	CloseDialogSlotWithScroll,
	Dialog,
	type DialogProps,
	Tooltip,
	Typography,
	Wrap,
	cn,
	formatNumber,
	getCSSVar,
	useMediaQuery,
	useNotifications,
} from "@tosize/ui";
import { RadioGroup } from "radix-ui";
import { type ReactNode, forwardRef, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { useConfig } from "../../context/ConfigContext";
import { useCabinetStore } from "../../context/StoreContext";
import { getImage, materialsSku } from "../../library/utilities";
import TitleBlock from "../TitleBlock";
import type { ApiData } from "../renderer/data";

export default function Material() {
	const intl = useIntl();
	const isDesktop = useMediaQuery({ min: "xl" });
	const { notify } = useNotifications();
	const { openSampleDialog, setOpenSampleDialog } = useConfig();
	const { state, api, setMaterial, setThickness } = useCabinetStore();

	const [openInnerSampleDialog, setOpenInnerSampleDialog] = useState(false);

	const onSampleDialogChange = (value: boolean) => {
		if (setOpenSampleDialog) {
			setOpenSampleDialog(value);
		}

		setOpenInnerSampleDialog(value);
	};

	const materials = useMemo(() => {
		return api?.materials.map((material, index) => ({
			...material,
			isHighlighted: index < 4,
			isActive: !!material.thicknesses?.some((t) => t?.sku === state.material),
		}));
	}, [api?.materials, state.material]);

	const [activeMaterial, highlightedMaterials, remainingMaterials] = useMemo(() => {
		const activeMaterial = materials?.find((m) => m.isActive);
		const highlightedMaterials = materials?.filter((m) => m.isHighlighted || m.isActive);
		const remainingMaterials = materials?.filter((m) => !(m.isHighlighted || m.isActive));

		return [activeMaterial, highlightedMaterials, remainingMaterials];
	}, [materials]);

	const onMaterialChange = (newMaterial: string) => {
		const material = materials?.find((m) => m.name === newMaterial);
		const thickness = material?.thicknesses?.find((t) => Number(t?.name) === state.thickness);
		const closestThickness = material?.thicknesses?.reduce((a, b) =>
			Math.abs(Number(b?.name) - state.thickness) < Math.abs(Number(a?.name) - state.thickness)
				? b
				: a,
		);
		const newThickness = thickness ?? closestThickness;

		if (!newThickness) {
			throw new Error("No thickness found");
		}

		if (!thickness) {
			notify({
				title: intl.formatMessage(
					{
						defaultMessage: "{thickness} is niet beschikbaar voor {material}",
					},
					{
						thickness: formatNumber({
							lang: intl.locale,
							value: state.thickness,
							unit: "millimeter",
							digits: { min: 0, max: 0 },
						}),
						material: material?.name,
					},
				),
				subtitle: intl.formatMessage(
					{
						defaultMessage: "Dikte is aangepast naar {value}",
					},
					{
						value: formatNumber({
							lang: intl.locale,
							value: Number(closestThickness?.name),
							unit: "millimeter",
							digits: { min: 0, max: 0 },
						}),
					},
				),
			});
		}

		setMaterial(newThickness.sku);
		setThickness(state, Number(newThickness.name), newThickness.sku);
	};

	if (materials === undefined) {
		return (
			<div className="animate-pulse">
				<div className="mb-2 flex w-full items-center justify-between border-shades border-b pb-2">
					<div className="h-5 w-1/2 rounded bg-shades" />
					<div className="h-5 w-1/4 rounded bg-shades" />
				</div>

				<div className="flex space-x-4">
					{Array.from({ length: 3 }).map((_, i) => (
						<div key={i} className="h-[54px] w-[54px] rounded-full bg-shades" />
					))}
				</div>
			</div>
		);
	}

	if (!materials?.length) {
		return null;
	}

	return (
		<div className="flex flex-col gap-y-1.5">
			<TitleBlock
				title={intl.formatMessage({ defaultMessage: "Materiaal" })}
				action={{
					title: intl.formatMessage({ defaultMessage: "Sample bestellen" }),
					onClick: () => onSampleDialogChange(true),
				}}
			/>

			<RadioGroup.Root
				className={cn("flex flex-wrap", {
					"mx-1 space-x-3": isDesktop,
					"flex-col space-y-2 sm:grid sm:grid-cols-2 sm:gap-4 md:grid-cols-3 xl:grid-cols-2":
						!isDesktop,
				})}
				disabled={materials?.length === 1}
				onValueChange={onMaterialChange}
				value={activeMaterial?.name}
			>
				{highlightedMaterials?.map((material, i) => (
					<RadioGroup.Item value={material.name} asChild key={i}>
						<MaterialItem
							material={material}
							title={intl.formatMessage({ defaultMessage: "Meer informatie" })}
						>
							<img
								width={48}
								height={48}
								alt={material.name}
								src={getImage(intl.locale, material)}
								className="h-10 w-10 rounded-full bg-shades object-cover xl:bg-shades-light"
							/>
						</MaterialItem>
					</RadioGroup.Item>
				))}

				{!!remainingMaterials?.length && (
					<MaterialItem
						onClick={() => onSampleDialogChange(true)}
						title={intl.formatMessage({ defaultMessage: "Bekijk alle materialen" })}
					>
						<div className="flex h-10 w-10 items-center justify-center rounded-full bg-shades object-cover xl:bg-white">
							<span>+{remainingMaterials.length}</span>
						</div>
					</MaterialItem>
				)}
			</RadioGroup.Root>

			{activeMaterial && (
				<div className="mt-2 rounded-full border-2 border-primary-medium bg-primary-medium px-3 py-2 text-center text-white leading-none">
					{activeMaterial.name}
				</div>
			)}

			<MaterialsDialog
				materials={materials}
				open={openSampleDialog ?? openInnerSampleDialog}
				onOpenChange={onSampleDialogChange}
			/>
		</div>
	);
}

interface MaterialsDialogProps extends Pick<DialogProps, "open" | "onOpenChange"> {
	materials: MaterialType[];
}

type MaterialType = ApiData["materials"][number] & { isHighlighted: boolean; isActive: boolean };

function MaterialsDialog(props: MaterialsDialogProps) {
	const { open, onOpenChange, materials } = props;

	const intl = useIntl();

	const [mdfMaterials, melamineMaterials] = useMemo(() => {
		const skus = [materialsSku.mdf, materialsSku.mdf_prime].flat();

		const mdfMaterials = materials.filter((m) =>
			m.thicknesses?.some((t) => (t?.sku ? skus.includes(t?.sku) : false)),
		);

		const melamineMaterials = materials.filter((m) =>
			m.thicknesses?.some((t) => (t?.sku ? !skus.includes(t?.sku) : false)),
		);

		return [mdfMaterials, melamineMaterials];
	}, [materials]);

	const onSampleItemClick = () => {
		const element = document.querySelector("#start-ordering");
		const top = (element?.getBoundingClientRect().top ?? 0) + window.scrollY;
		const headerHeight = getCSSVar("--header-y");

		window.scrollTo({ top: top - headerHeight, behavior: "smooth" });

		props.onOpenChange(false);
	};

	return (
		<Dialog
			width="4xl"
			title={intl.formatMessage({ defaultMessage: "Materialen" })}
			open={open}
			onOpenChange={onOpenChange}
		>
			<div className="flex flex-col gap-6">
				{!!mdfMaterials?.length && (
					<div className="flex flex-col">
						<Typography variant="paragraph-bold" className="mb-2 text-lg">
							{intl.formatMessage({ defaultMessage: "Zelf afwerken" })}
						</Typography>

						{mdfMaterials?.map((material) => (
							<SampleItem
								key={material.id}
								material={material}
								onClose={() => onSampleItemClick()}
							/>
						))}
					</div>
				)}

				{!!melamineMaterials?.length && (
					<div className="flex flex-col">
						<Typography variant="paragraph-bold" className="mb-2 text-lg">
							{intl.formatMessage({ defaultMessage: "Volledig afgewerkt" })}
						</Typography>

						{melamineMaterials.map((material) => (
							<SampleItem
								key={material.id}
								material={material}
								onClose={() => onSampleItemClick()}
							/>
						))}
					</div>
				)}

				<div className="flex items-center justify-center">
					<CloseDialogSlotWithScroll id="start-ordering">
						<Button color="success">
							{intl.formatMessage({ defaultMessage: "Start met configureren" })}
						</Button>
					</CloseDialogSlotWithScroll>
				</div>
			</div>
		</Dialog>
	);
}

interface MaterialProps {
	material?: ApiData["materials"][number];
	title: string;
	children: ReactNode;
}

type MaterialItemProps = React.ButtonHTMLAttributes<HTMLButtonElement> & MaterialProps;

const MaterialItem = forwardRef<HTMLButtonElement, MaterialItemProps>(function ItemRef(props, ref) {
	const { material, title, children, className, ...restProps } = props;
	const isDesktop = useMediaQuery({ min: "xl" });

	return (
		<button
			ref={ref}
			className={cn("group flex items-center focus:outline-none motion-safe:transition", className)}
			{...restProps}
		>
			<div className="shrink-0 overflow-hidden rounded-full border border-shades-medium p-0.5 ring-1 ring-transparent group-hover:border-primary-dark group-focus-visible:border-primary-dark group-rdx-state-checked:border-primary-dark group-rdx-state-checked:bg-white group-rdx-state-checked:ring-2 group-rdx-state-checked:ring-primary-dark">
				<Wrap
					if={isDesktop}
					with={(children) => (
						<MaterialTooltip material={material} title={title}>
							{children}
						</MaterialTooltip>
					)}
				>
					{children}
				</Wrap>
			</div>
			{!isDesktop && <div className="ml-4 text-start leading-tight">{material?.name ?? title}</div>}
		</button>
	);
});

function MaterialTooltip(props: MaterialProps & { hideName?: boolean }) {
	const { material, title, children, hideName = false } = props;
	const intl = useIntl();
	const target = material?.thicknesses?.some((t) => materialsSku.mdf.includes(t?.sku as string))
		? "#mdf-natural"
		: material?.thicknesses?.some((t) => materialsSku.mdf_prime.includes(t?.sku as string))
			? "#mdf-prime"
			: "#furniture-panel";

	return (
		<Tooltip
			title={material?.name}
			color="primary-dark"
			sideOffset={2.5}
			hideOnTouchDevice
			className={material ? "p-2" : undefined}
			side="top"
			content={
				material ? (
					<div className="flex flex-col gap-2">
						<div className="flex flex-wrap justify-between gap-x-2">
							{!hideName && <span>{material.name}</span>}

							<Anchor className="text-white underline" href={target}>
								{title}
							</Anchor>
						</div>

						<img
							width={620}
							height={310}
							alt={title}
							src={getImage(intl.locale, material, true)}
							className="aspect-[2/1] object-cover"
						/>
					</div>
				) : (
					<div className="cursor-pointer text-white underline">{title}</div>
				)
			}
			allowTriggerClick
		>
			{children}
		</Tooltip>
	);
}

interface SampleItemProps {
	material: MaterialType;
	onClose: () => void;
}

function SampleItem({ material, onClose }: SampleItemProps) {
	const intl = useIntl();
	const { onAddSampleToCart } = useConfig();
	const { state, setMaterial, setThickness } = useCabinetStore();
	const [isLoading, setIsLoading] = useState(false);

	function onSetMaterial() {
		const newThickness =
			material?.thicknesses?.find((t) => Number(t?.name) === state.thickness) ??
			material?.thicknesses?.at(-1);

		if (newThickness) {
			setMaterial(newThickness.sku);
			setThickness(state, Number(newThickness.name), newThickness.sku);
		}

		onClose();
	}

	const target = material.thicknesses?.some((t) => materialsSku.mdf.includes(t?.sku as string))
		? "#mdf-natural"
		: material.thicknesses?.some((t) => materialsSku.mdf_prime.includes(t?.sku as string))
			? "#mdf-prime"
			: "#furniture-panel";

	return (
		<div className="flex items-start gap-4 p-2 even:bg-shades-light sm:flex-row sm:items-center sm:gap-4 sm:px-4">
			<Tooltip
				hideOnTouchDevice
				color="primary-dark"
				className="p-2"
				content={
					<Anchor className="flex flex-col gap-2" href={target}>
						<div className="flex justify-between gap-x-2 text-white">
							{material.isHighlighted && (
								<div className="font-bold">
									{intl.formatMessage({ defaultMessage: "Favoriet!" })}
								</div>
							)}

							<div>
								{intl.formatMessage({
									defaultMessage: "Klik om dit decor te selecteren",
								})}
							</div>
						</div>

						<img
							width={620}
							height={310}
							alt={intl.formatMessage({
								defaultMessage: "Klik om dit decor te selecteren",
							})}
							src={getImage(intl.locale, material, true)}
							className="aspect-[2/1] object-cover"
						/>
					</Anchor>
				}
			>
				<button
					type="button"
					onClick={() => onSetMaterial()}
					className={cn(
						"shrink-0 appearance-none overflow-hidden rounded-full p-0.5 motion-safe:transition-colors",
						{
							"enter:ring-primary-dark ring-1 ring-shades-medium": !(
								material.isHighlighted || material.isActive
							),
							"enter:ring-secondary ring-2 ring-primary-dark": !!material.isActive,
							"enter:ring-success-medium ring-2 ring-success": material.isHighlighted,
						},
					)}
				>
					<img
						width={48}
						height={48}
						alt={material.name}
						src={getImage(intl.locale, material)}
						className="h-8 w-8 rounded-full object-cover sm:h-12 sm:w-12"
					/>
				</button>
			</Tooltip>

			<div className="flex flex-col gap-x-4 sm:w-full sm:flex-row sm:justify-between">
				<button
					onClick={() => onSetMaterial()}
					type="button"
					className={cn("appearance-none text-start enter:underline", {
						"font-bold": !!material.isActive,
					})}
				>
					{material.name}
				</button>

				<div className="flex shrink-0 flex-wrap gap-x-4 sm:flex-nowrap sm:items-center">
					<Button
						className="shrink-0"
						variant="link"
						isLoading={isLoading}
						starticon={faCartPlus}
						onClick={async () => {
							if (onAddSampleToCart && material.product?.sampleProduct?.id) {
								setIsLoading(true);

								await onAddSampleToCart(material.product?.sampleProduct?.id);

								setIsLoading(false);

								onClose();
							}
						}}
					>
						{intl.formatMessage({
							defaultMessage: "Sample bestellen",
						})}
					</Button>

					<Anchor
						className="shrink-0"
						href={material.product?.url}
						target="_blank"
						variant="link"
						starticon={faInfo}
					>
						{intl.formatMessage({
							defaultMessage: "Meer informatie",
						})}
					</Anchor>
				</div>
			</div>
		</div>
	);
}
